import { useAuth } from "@wojo/auth";
import currency from "currency.js";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useTripBuilder } from "./TripBuilderContext";
import { useStartQuote } from "./use-start-quote";
import { TripBuilderMessageViewUnavailable } from "../message-views/TripBuilderMessageViewUnavailable";
import { TripBuilderLoadingQuote } from "../trip-builder-quote-step/LoadingQuote";
import { TripBuilderQuoteForm } from "./TripBuilderQuoteForm";
import { useDraftQuoteFragment } from "./use-draft-quote-fragment";
import { useHandleCustomerChange } from "./use-handle-customer-change";
import { TripBuilderDraftQuoteFragment } from "../../../../generated/graphql";
import { useDraftQuoteAnalytics } from "./use-draft-quote-analytics";

type TripBuilderQuoteStepContextValue = React.PropsWithChildren<{
    departureId?: string | null;
    draftId?: string | null;
    error?: string;
    draftQuote?: TripBuilderDraftQuoteFragment | null;
    setDraftId: React.Dispatch<React.SetStateAction<string | null>>;
    navigationLoading: boolean;
}>;

const TripBuilderQuoteContext = createContext<TripBuilderQuoteStepContextValue>(
    null!,
);

type TripBuilderQuoteContextProviderProps = React.PropsWithChildren;

export const TripBuilderQuoteContextProvider: React.FC<
    TripBuilderQuoteContextProviderProps
> = ({ children }) => {
    const [draftId, setDraftId] = useState<string | null>(null);
    const { departureId, departure } = useTripBuilder();
    const { hasError } = useStartQuote(draftId, setDraftId);
    const { customer } = useAuth();
    const [error, setError] = useState("");
    const [navigationLoading, setNavigationLoading] = useState(false);
    const draftQuote = useDraftQuoteFragment(draftId);
    const handleCustomerChange = useHandleCustomerChange(draftId, setError);

    useDraftQuoteAnalytics(draftQuote);

    useEffect(() => {
        const hasDraftCustomerId = !!draftQuote?.order.customerId;

        if (!!customer?._id && !!draftQuote && !hasDraftCustomerId) {
            handleCustomerChange(customer._id);
        }
    }, [
        customer?._id,
        draftQuote,
        draftQuote?.order.customerId,
        handleCustomerChange,
    ]);

    if (hasError) {
        return <TripBuilderMessageViewUnavailable />;
    }

    if (!departure || !draftId) {
        return <TripBuilderLoadingQuote />;
    }
    return (
        <TripBuilderQuoteForm
            setError={setError}
            navigationLoadingTimeout={() => {
                setNavigationLoading(true);
                setTimeout(() => {
                    setNavigationLoading(false);
                }, 5000);
            }}
            draftId={draftId}
        >
            <TripBuilderQuoteContext.Provider
                value={{
                    departureId,
                    draftQuote,
                    error,
                    setDraftId,
                    navigationLoading,
                }}
            >
                {children}
            </TripBuilderQuoteContext.Provider>
        </TripBuilderQuoteForm>
    );
};

export const useTripBuilderQuote = () => {
    const context = useContext(TripBuilderQuoteContext);
    const draftQuote =
        context.draftQuote?.order.__typename === "Quote"
            ? {
                  ...context.draftQuote,
                  order: context.draftQuote.order,
              }
            : null;
    const trip = draftQuote?.order.trips.at(0);
    const rolledUpTourPrice = [
        ...(trip?.openFeeLineItems ?? []),
        ...(trip?.openAdjustmentLineItems ?? []),
        ...(trip?.openPrivateGroupLineItem
            ? [trip.openPrivateGroupLineItem]
            : []),
    ]
        .filter(
            (lineItem) =>
                lineItem.rollsUpToTarget &&
                lineItem.targetLineItemId === trip?.tourLineItem?._id,
        )
        .reduce(
            (acc, lineItem) => acc.add(lineItem.price.value),
            currency(trip?.tourLineItem?.price.value ?? 0),
        ).value;

    return {
        ...context,
        rolledUpTourPrice,
        draftQuote,
    };
};
